// Owner Cockpit — super_admin landing (John & Priya).
// Slice A: top-strip with 5 pulse tiles.
// Slice B: three persona scorecards (Kalpana / Vendors / Ops).
// Recent Events timeline lands as Slice C on this same page.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { cockpitApi } from '../api/services';
import InfoTip from '../components/InfoTip';
import DrillModal, { useDrill } from '../components/DrillModal';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  Loader2, RefreshCcw, Inbox, Send, PackageCheck,
  AlertTriangle, IndianRupee, ArrowDown, ArrowUp, Minus,
  UserCog, Store, Truck, Activity, XCircle, Pause, Repeat,
  Banknote, AlertOctagon, Package, Sparkles, Download,
} from 'lucide-react';

// Plain-English meaning of each exception reason, for the "i" icons on the
// Kalpana scorecard's "Real exceptions handled" list.
const REASON_INFO = {
  hold_requested: 'The customer (or the team) asked to pause this order before dispatch. It waits, owned by the dispatch team, until the hold is released.',
  hold_released: 'A held order was released back into the normal flow.',
  order_cancelled: 'The order was cancelled. What remains is refund tracking (see the order page).',
  vendor_oos_need_alt: 'The vendor reported a product out of stock. The customer must be offered an alternative — the order waits on that conversation.',
  customer_clarified_alt: 'The customer accepted the offered alternative product, so the order moved forward with it.',
  customer_refused_alt: 'The customer refused the offered alternative — this usually ends in a cancellation and refund.',
  address_correction_approved: 'A wrong or incomplete shipping address was corrected and approved, clearing the order for dispatch.',
  reship_needs_linkage: 'A reship arrived from MBO but is not yet linked to its original order. Linking matters for billing: it decides which reship percentage the vendor charges us.',
  reship_linked: 'A reship was linked to its original order, so the right billing policy (e.g. 50% for lost in transit) applies in Vendor Payables.',
  virtual_clarified: 'A placeholder ("virtual") product line was clarified into a real product so it can be dispatched.',
  manual_substitute: 'The team manually substituted a product on the order (e.g. a different brand of the same medicine).',
  substitute_approved: 'A proposed product substitute was approved for dispatch.',
  vendor_delayed: 'The vendor said dispatch will be late. The order keeps waiting with the vendor — if one vendor shows this often, that is worth a conversation.',
  vendor_wrong_product: 'The vendor shipped the wrong product. This triggers a reship where the vendor decides the product charge case by case (bills ₹0 until agreed).',
};

// Owner-facing labels and icons for each significant handoff reason. Keep
// the prose short and operational — "Order cancelled" beats the raw
// "order_cancelled" identifier when an owner is scanning a list.
const EVENT_META = {
  order_cancelled:             { label: 'Cancelled',                   tone: 'rose',    Icon: XCircle },
  hold_requested:              { label: 'Put on hold',                 tone: 'amber',   Icon: Pause },
  hold_released:               { label: 'Hold released',               tone: 'emerald', Icon: Pause },
  vendor_oos_need_alt:         { label: 'Vendor out of stock',         tone: 'amber',   Icon: AlertOctagon },
  customer_clarified_alt:      { label: 'Customer accepted alt',       tone: 'emerald', Icon: PackageCheck },
  customer_refused_alt:        { label: 'Customer refused alt',        tone: 'rose',    Icon: XCircle },
  address_correction_approved: { label: 'Address correction approved', tone: 'indigo',  Icon: PackageCheck },
  reship_needs_linkage:        { label: 'Reship awaiting link',        tone: 'amber',   Icon: Repeat },
  reship_linked:               { label: 'Reship linked',               tone: 'emerald', Icon: Repeat },
  virtual_clarified:           { label: 'Virtual product clarified',   tone: 'indigo',  Icon: PackageCheck },
  manual_substitute:           { label: 'Manual substitute',           tone: 'indigo',  Icon: Package },
  substitute_approved:         { label: 'Substitute approved',         tone: 'emerald', Icon: PackageCheck },
  vendor_delayed:              { label: 'Vendor delayed',              tone: 'amber',   Icon: AlertOctagon },
  vendor_wrong_product:        { label: 'Wrong product shipped',       tone: 'rose',    Icon: AlertOctagon },
  partial_shipment_recorded:   { label: 'Partial shipment',            tone: 'indigo',  Icon: Package },
  refund_marked_paid:          { label: 'Refund paid',                 tone: 'emerald', Icon: Banknote },
};

function todayIso() { return new Date().toISOString().slice(0, 10); }

function fmtNum(n) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString('en-IN');
}

// INR formatter that switches to lakh notation above 1 lakh so big numbers
// stay readable (₹12,79,556 → ₹12.80 L). Below 1 lakh use plain commas.
function fmtInr(n) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (v >= 10_000_000) return `₹${(v / 10_000_000).toFixed(2)} Cr`;
  if (v >= 100_000)    return `₹${(v / 100_000).toFixed(2)} L`;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

// Show today vs yesterday delta with arrow + colour. Positive delta on
// "received" is good; on "stuck" it's bad. Caller picks the polarity.
function Delta({ today, yesterday, positiveIsGood = true }) {
  if (today == null || yesterday == null) return null;
  const diff = today - yesterday;
  if (diff === 0) return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
      <Minus className="w-3 h-3" /> same as yesterday
    </span>
  );
  const up = diff > 0;
  const good = positiveIsGood ? up : !up;
  const Icon = up ? ArrowUp : ArrowDown;
  const cls = good ? 'text-emerald-600' : 'text-rose-600';
  const pct = yesterday === 0 ? null : Math.abs(Math.round((diff / yesterday) * 100));
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${cls}`}>
      <Icon className="w-3 h-3" /> {Math.abs(diff)} {pct != null && <>({pct}%)</>} vs yesterday
    </span>
  );
}

function Tile({ icon: Icon, label, value, accent = 'blue', info, infoAlign = 'center', children }) {
  const accents = {
    blue:    'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    indigo:  'bg-indigo-50 text-indigo-700',
    rose:    'bg-rose-50 text-rose-700',
    amber:   'bg-amber-50 text-amber-700',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${accents[accent]}`}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</span>
        {info && <InfoTip title={label} align={infoAlign}>{info}</InfoTip>}
      </div>
      <div className="text-3xl font-semibold text-gray-900 tabular-nums">{value}</div>
      <div className="text-xs text-gray-600 leading-snug">{children}</div>
    </div>
  );
}

export default function OwnerCockpit() {
  const [asOf, setAsOf] = useState(todayIso());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expandStuck, setExpandStuck] = useState(false);
  const [tab, setTab] = useState('overview');
  // Generic drill-down behind any cockpit number (shared hook + modal).
  const { drill, drillData, drillLoading, openDrill, exportDrill, closeDrill } = useDrill(asOf);
  const [digest, setDigest] = useState(null);

  useEffect(() => {
    setDigest(null);
    cockpitApi.digest({ date: asOf }).then((r) => setDigest(r.data)).catch(() => {});
  }, [asOf]);

  async function load() {
    setLoading(true);
    try {
      const res = await cockpitApi.pulse({ date: asOf });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load cockpit');
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [asOf]);

  const p = data?.pulse;
  const eventsTotal = data?.events?.total ?? 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Owner Cockpit</h1>
          <p className="text-sm text-gray-500">
            At-a-glance health check across Kalpana, vendors, and Ops. View-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">As of</span>
            <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)}
                   max={todayIso()}
                   className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
          </label>
          <button onClick={load} disabled={loading}
                  className="flex items-center gap-1 px-3 py-2 mt-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {!data && loading && (
        <div className="py-16 text-center text-sm text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {data && (
        <div className="flex items-center gap-1 mt-4 border-b border-gray-200">
          <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>
            Overview
          </TabButton>
          <TabButton active={tab === 'week'} onClick={() => setTab('week')}>
            This week
          </TabButton>
          <TabButton active={tab === 'events'} onClick={() => setTab('events')}>
            Recent events
            {eventsTotal > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-semibold rounded-full bg-gray-900 text-white">
                {eventsTotal}
              </span>
            )}
          </TabButton>
        </div>
      )}

      {p && tab === 'overview' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
            <Tile icon={Inbox} label="Orders received" accent="blue"
                  infoAlign="right"
                  info="Orders that arrived in Dispatch (uploaded from MBO) on the chosen day. The 7-day and month numbers use the same rule. A split order counts each part separately, because each part travels on its own."
                  value={fmtNum(p.ordersReceived.today)}>
              <Delta today={p.ordersReceived.today} yesterday={p.ordersReceived.yesterday} positiveIsGood />
              <div className="mt-1 text-gray-500">
                {fmtNum(p.ordersReceived.last7d)} last 7d · {fmtNum(p.ordersReceived.mtd)} this month
              </div>
            </Tile>

            <Tile icon={Send} label="Dispatched to vendor" accent="indigo"
                  info="An order counts as dispatched the moment its dispatch file is emailed to the vendor. Each order counts once, even if it has more than one shipment."
                  value={fmtNum(p.ordersDispatched.today)}>
              <Delta today={p.ordersDispatched.today} yesterday={p.ordersDispatched.yesterday} positiveIsGood />
              <div className="mt-1 text-gray-500">
                {fmtNum(p.ordersDispatched.last7d)} last 7d · {fmtNum(p.ordersDispatched.mtd)} this month
              </div>
            </Tile>

            <Tile icon={PackageCheck} label="Delivered" accent="emerald"
                  info="Orders India Post marked as delivered on that day. We poll India Post tracking every 4 hours, so a delivery can show up here a few hours after it happens."
                  value={fmtNum(p.ordersDelivered.today)}>
              <Delta today={p.ordersDelivered.today} yesterday={p.ordersDelivered.yesterday} positiveIsGood />
              <div className="mt-1 text-gray-500">
                {fmtNum(p.ordersDelivered.last7d)} last 7d · {fmtNum(p.ordersDelivered.mtd)} this month
              </div>
            </Tile>

            <Tile icon={AlertTriangle} label="Stuck > 5 days" accent={p.stuckOver5d.total > 0 ? 'rose' : 'emerald'}
                  info="Active orders that have been sitting with the same team for more than 5 days with no movement. Anything here means somebody needs to act — click below to see which team is holding them. Completed, cancelled and delivered orders are excluded."
                  value={fmtNum(p.stuckOver5d.total)}>
              {p.stuckOver5d.total === 0 ? (
                <span className="text-emerald-700">Nothing aged in the queue — clean.</span>
              ) : (
                <>
                  <button onClick={() => setExpandStuck((v) => !v)}
                          className="text-blue-600 hover:underline text-xs">
                    {expandStuck ? 'hide breakdown' : 'who has them?'}
                  </button>
                  {expandStuck && (
                    <>
                      <ul className="mt-1 space-y-0.5">
                        {p.stuckOver5d.byTeam.map((b) => (
                          <li key={b.team}>
                            <button onClick={() => openDrill('stuck_over_5d', { team: b.team })}
                                    title="See which orders these are"
                                    className="w-full flex justify-between rounded px-1 -mx-1 hover:bg-rose-50 text-left">
                              <span className="capitalize text-blue-600 hover:underline">{b.team.replace(/_/g, ' ')}</span>
                              <span className="font-medium tabular-nums">{b.count}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                      <button onClick={() => openDrill('stuck_over_5d')}
                              className="mt-1 text-blue-600 hover:underline text-xs">
                        see all {fmtNum(p.stuckOver5d.total)} orders →
                      </button>
                    </>
                  )}
                </>
              )}
            </Tile>

            <Tile icon={IndianRupee} label="INR payable · this month" accent="amber"
                  infoAlign="left"
                  info="What we owe vendors for orders dispatched this month: quantity × the vendor's INR unit price that was in effect on the dispatch date, plus shipping. Billing is settled at month end. 'Unpriced lines' are dispatched products with no price on file yet — until they are priced, this total is understated."
                  value={fmtInr(p.inrPayablesMtd.totalDueInr)}>
              <div className="text-gray-600">
                <strong>{fmtInr(p.inrPayablesMtd.totalInr)}</strong> line cost · <strong>{fmtInr(p.inrPayablesMtd.shippingInr)}</strong> shipping
              </div>
              <div className="mt-1 text-gray-500">
                {p.inrPayablesMtd.vendorCount} vendor(s) ·{' '}
                {p.inrPayablesMtd.periodFrom} → {p.inrPayablesMtd.periodTo}
              </div>
              {p.inrPayablesMtd.unpricedLines > 0 && (
                <div className="mt-1 text-amber-700">
                  {fmtNum(p.inrPayablesMtd.unpricedLines)} line(s) unpriced — fix on Vendors page.
                </div>
              )}
            </Tile>
          </div>

          {p.unpricedDispatched?.productCount > 0 && (
            <UnpricedAlert up={p.unpricedDispatched} />
          )}

          {data.scorecards && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-6">
              <KalpanaScorecard sc={data.scorecards.kalpana} onDrill={openDrill} />
              <VendorsScorecard vendors={data.scorecards.vendors} onDrill={openDrill} />
              <OpsScorecard ops={data.scorecards.ops} onDrill={openDrill} />
            </div>
          )}
        </>
      )}

      {data && tab === 'week' && (
        <DigestTab digest={digest} onDrill={openDrill} />
      )}

      {data && tab === 'events' && (
        <EventsTab events={data.events} />
      )}

      {drill && (
        <DrillModal
          data={drillData}
          loading={drillLoading}
          onClose={closeDrill}
          onExport={exportDrill}
        />
      )}
    </div>
  );
}

// "This week" digest tab — the 7-day story for an owner who doesn't log in
// daily. Every number opens its drill; worst-stuck orders link directly.
function DigestTab({ digest, onDrill }) {
  if (!digest) {
    return (
      <div className="py-12 text-center text-sm text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Building the week…
      </div>
    );
  }
  const N = digest.numbers;
  const tiles = [
    { label: 'Received', ...N.received, tone: 'text-gray-900',
      info: 'Orders that arrived from MBO during these 7 days. A split order counts each part separately.' },
    { label: 'Dispatched', ...N.dispatched, tone: 'text-gray-900',
      info: 'Orders whose dispatch file was emailed to a vendor during these 7 days. Each order counts once even with multiple parcels.' },
    { label: 'Delivered', ...N.delivered, tone: 'text-emerald-700',
      info: 'Orders India Post marked delivered during these 7 days. We poll tracking every 4 hours, so the latest deliveries can lag a few hours.' },
    { label: 'Returned', ...N.returned, tone: N.returned.value > 0 ? 'text-rose-700' : 'text-gray-900',
      info: 'Parcels India Post sent back to the vendor during these 7 days. Each one usually means a refund or reship decision.' },
    { label: 'Exceptions handled', ...N.exceptions, tone: 'text-gray-900',
      info: 'Judgement calls the dispatch team made this week — holds, cancellations, out-of-stock, substitutes, reships. Routine steps are not counted.' },
    { label: 'Stuck > 5 days', ...N.stuck, tone: N.stuck.value > 0 ? 'text-rose-700' : 'text-emerald-700',
      info: 'Active orders sitting with the same team for more than 5 days RIGHT NOW. This is the current backlog, not a weekly count — it should trend to zero.' },
  ];
  const pw = digest.payableThisWeek;
  return (
    <div className="mt-4 space-y-4">
      <div className="text-xs text-gray-500">
        Week of <strong>{digest.windowFrom}</strong> → <strong>{digest.asOf}</strong>.
        Click any number for the orders behind it; every list exports.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map((t) => (
          <div key={t.label}
               className="bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-300 hover:bg-blue-50/40">
            <div className="flex items-center justify-between gap-1">
              <button onClick={() => onDrill(t.drill)} title="See which orders these are"
                      className="text-[11px] uppercase tracking-wide text-blue-600 hover:underline text-left">
                {t.label}
              </button>
              <InfoTip title={t.label}>{t.info}</InfoTip>
            </div>
            <button onClick={() => onDrill(t.drill)} title="See which orders these are"
                    className={`text-2xl font-semibold tabular-nums ${t.tone} hover:underline`}>
              {fmtNum(t.value)}
            </button>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">
          Payable added this week{' '}
          <InfoTip title="Payable added this week" align="right">
            What the orders dispatched in these 7 days will cost us across all
            vendors — products + shipping at the prices effective on each
            dispatch date. Click through to the Vendor Payables page for the
            per-vendor and per-line breakdown of exactly this week.
          </InfoTip>
        </div>
        <Link to={`/admin/vendor-payables?from=${pw.from}&to=${pw.to}`}
              className="text-2xl font-semibold text-gray-900 hover:text-blue-700 hover:underline tabular-nums">
          {fmtInr(pw.totalDueInr)}
        </Link>
        <div className="text-xs text-gray-500 mt-1">
          {fmtInr(pw.lineCostInr)} products + {fmtInr(pw.shippingInr)} shipping
          {pw.reshipInr > 0 && <> · of which <span className="text-violet-700">{fmtInr(pw.reshipInr)} reships</span></>}
          {' '}· {pw.vendorCount} vendor(s)
        </div>
      </div>

      {digest.worstStuck.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold flex items-center justify-between">
            <span>
              Worst stuck orders right now{' '}
              <InfoTip title="Worst stuck orders">
                The three orders that have sat longest with the same owner,
                with the reason they're parked there. These are the first
                three things to ask the team about. "See all" opens the full
                list with export.
              </InfoTip>
            </span>
            <button onClick={() => onDrill('stuck_over_5d')}
                    className="text-xs font-normal text-blue-600 hover:underline">
              see all →
            </button>
          </div>
          <ul className="divide-y divide-gray-100">
            {digest.worstStuck.map((r) => (
              <li key={r.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                <Link to={`/orders/${r.id}`} className="font-mono text-blue-600 hover:underline shrink-0">
                  {r.order_id_external}
                </Link>
                <span className="text-gray-600 truncate">{r.customer_name}</span>
                <span className="text-gray-500 text-xs truncate flex-1">
                  {r.reason_label} · with <span className="capitalize">{(r.team || '').replace(/_/g, ' ')}</span>
                  {(r.owner_user_name || r.owner_vendor_name) && ` (${r.owner_user_name || r.owner_vendor_name})`}
                </span>
                <span className="font-semibold text-rose-600 tabular-nums shrink-0">{r.days_stuck}d</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Payables-gap alert — dispatched lines this month the pricing lookup can't
// price. Computed live, split by fix path: a pending AI match just needs
// approval on the Vendor Mapping page; no match at all means Kalpana chases
// the vendor for a price quote.
function UnpricedAlert({ up }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="w-4 h-4 text-rose-600" />
        <span className="text-sm font-semibold text-rose-900">
          {up.lineCount} dispatched line(s) across {up.productCount} product(s) can't be priced this month
        </span>
        {up.fixableByApprove > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white font-medium">
            {up.fixableByApprove} fixable — approve AI match
          </span>
        )}
        {up.needsQuote > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-rose-600 text-white font-medium">
            {up.needsQuote} need a vendor price quote
          </span>
        )}
        <button onClick={() => setExpanded((v) => !v)}
                className="ml-auto text-xs text-rose-700 hover:underline">
          {expanded ? 'hide list' : 'show list'}
        </button>
      </div>
      <p className="text-xs text-rose-800 mt-1">
        These lines bill ₹0 in Vendor Payables until fixed. "Approve AI match" ones
        resolve in one click on the Vendor Mapping page; the rest need the vendor to
        quote a price (quote-request download on the same page).
      </p>
      {expanded && (
        <ul className="mt-2 space-y-1 text-xs text-gray-800">
          {up.items.map((it, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="font-medium">{it.indian_brand || it.product_name}</span>
              {it.indian_brand && it.product_name !== it.indian_brand && (
                <span className="text-gray-500">"{it.product_name}"</span>
              )}
              <span className="text-gray-500">({it.vendor_name})</span>
              <span className="text-gray-500">{it.line_count} line(s) · qty {it.total_qty}</span>
              {it.has_ai_match ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">AI match ready</span>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">needs quote</span>
              )}
            </li>
          ))}
          {up.productCount > up.items.length && (
            <li className="text-gray-500">
              …and {up.productCount - up.items.length} more product(s) — see the Vendor Mapping page.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button onClick={onClick}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium ${
              active ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
      {children}
    </button>
  );
}

function EventsTab({ events }) {
  if (!events) return null;
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const e of events.events) {
      const day = String(e.at).slice(0, 10);
      if (!buckets.has(day)) buckets.set(day, []);
      buckets.get(day).push(e);
    }
    return Array.from(buckets.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [events]);

  const summaryRows = Object.entries(events.summary || {})
    .sort((a, b) => b[1] - a[1]);

  if (events.total === 0) {
    return (
      <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
        No significant events in the last {events.daysBack} days.
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-3">
        <div className="text-xs text-gray-500 mb-2">
          <Activity className="w-3.5 h-3.5 inline mr-1" />
          {events.total} significant event(s) since <strong>{events.windowFrom}</strong>{' '}
          <InfoTip title="What counts as significant?" align="right">
            Only the moments an owner would care about: cancellations, holds,
            out-of-stock, substitutes, reships, refunds, wrong or partial
            shipments. Routine steps (order arriving, dispatching, delivering)
            are left out so this list stays reviewable. Every entry shows who
            did it and links to the order.
          </InfoTip>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {summaryRows.map(([reason, count]) => {
            const meta = EVENT_META[reason] || { label: reason, tone: 'gray' };
            return (
              <span key={reason}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${toneBg(meta.tone)}`}>
                {meta.label} · <strong>{count}</strong>
              </span>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        {grouped.map(([day, dayEvents]) => (
          <div key={day} className="p-3">
            <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
              {new Date(day).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
              <span className="ml-2 text-gray-400">({dayEvents.length})</span>
            </div>
            <ul className="space-y-1.5">
              {dayEvents.map((e, i) => {
                const meta = EVENT_META[e.reasonCode] || { label: e.reasonCode, tone: 'gray', Icon: Activity };
                const Icon = meta.Icon;
                const time = new Date(e.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
                return (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${toneBg(meta.tone)} shrink-0 mt-0.5`}>
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-xs text-gray-400 font-mono tabular-nums">{time}</span>
                        <span className={`text-xs font-medium ${toneText(meta.tone)}`}>{meta.label}</span>
                        <Link to={`/orders/${e.orderId}`}
                              className="text-xs font-mono text-blue-700 hover:underline">
                          {e.orderIdExternal}
                        </Link>
                        <span className="text-xs text-gray-500 truncate">
                          {e.customerName}{e.vendorName ? ` · ${e.vendorName}` : ''}
                        </span>
                      </div>
                      {e.reasonNote && (
                        <div className="text-xs text-gray-600 italic mt-0.5">"{e.reasonNote}"</div>
                      )}
                      <div className="text-[11px] text-gray-400 mt-0.5">by {e.actor}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function toneBg(tone) {
  switch (tone) {
    case 'rose':    return 'bg-rose-50 text-rose-700';
    case 'amber':   return 'bg-amber-50 text-amber-700';
    case 'emerald': return 'bg-emerald-50 text-emerald-700';
    case 'indigo':  return 'bg-indigo-50 text-indigo-700';
    default:        return 'bg-gray-100 text-gray-700';
  }
}
function toneText(tone) {
  switch (tone) {
    case 'rose':    return 'text-rose-700';
    case 'amber':   return 'text-amber-700';
    case 'emerald': return 'text-emerald-700';
    case 'indigo':  return 'text-indigo-700';
    default:        return 'text-gray-700';
  }
}

function ScorecardShell({ icon: Icon, title, subtitle, accent = 'blue', info, infoAlign = 'center', children }) {
  const accents = {
    blue:    'bg-blue-50 text-blue-700 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    indigo:  'bg-indigo-50 text-indigo-700 border-indigo-100',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
      <div className={`px-4 py-3 border-b flex items-center gap-2 ${accents[accent]}`}>
        <Icon className="w-4 h-4" />
        <div className="text-sm font-semibold">{title}</div>
        {info && <InfoTip title={title} align={infoAlign}>{info}</InfoTip>}
        {subtitle && <div className="text-xs opacity-70 ml-auto">{subtitle}</div>}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-3 text-sm">{children}</div>
    </div>
  );
}

function Row({ label, value, hint, alert, onClick }) {
  const body = (
    <>
      <div className={`text-xs flex-1 text-left ${onClick ? 'text-blue-600' : 'text-gray-500'}`}>{label}</div>
      <div className={`text-base font-semibold tabular-nums ${alert ? 'text-rose-700' : 'text-gray-900'}`}>{value}</div>
      {hint && <div className="text-[11px] text-gray-500 w-20 text-right">{hint}</div>}
    </>
  );
  if (onClick) {
    return (
      <button onClick={onClick} title="See which orders these are"
              className="flex items-baseline gap-2 w-full rounded px-1 -mx-1 hover:bg-blue-50">
        {body}
      </button>
    );
  }
  return <div className="flex items-baseline gap-2">{body}</div>;
}

function HoursDelta({ last7d, prior7d }) {
  if (last7d == null) return null;
  if (prior7d == null) {
    return <span className="text-xs text-gray-500">no prior-week comparison</span>;
  }
  const diff = last7d - prior7d;
  if (Math.abs(diff) < 0.5) return <span className="inline-flex items-center gap-1 text-xs text-gray-500"><Minus className="w-3 h-3" /> flat vs prior 7d</span>;
  const up = diff > 0; // up = slower → bad
  const Icon = up ? ArrowUp : ArrowDown;
  const cls = up ? 'text-rose-600' : 'text-emerald-600';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${cls}`}>
      <Icon className="w-3 h-3" /> {Math.abs(diff).toFixed(1)}h vs prior 7d
    </span>
  );
}

function KalpanaScorecard({ sc, onDrill }) {
  if (!sc) return null;
  const overAlertCount = sc.aging.aged_gt_7d + sc.aging.aged_24_48h;
  return (
    <ScorecardShell icon={UserCog} title="Kalpana — Dispatch Admin" accent="blue"
                    infoAlign="right"
                    info="How the Dispatch Admin's desk looks right now. 'Waiting' counts orders currently owned by the dispatch team that need her decision. The speed metric is the average hours from an order arriving from MBO to it being emailed to a vendor (last 7 days, compared to the 7 days before). 'Real exceptions' counts the holds, cancellations, out-of-stock and similar judgement calls she handled this week."
                    subtitle={`${sc.waitingTotal} waiting`}>
      <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
          Waiting on her decision{' '}
          <InfoTip title="Waiting on her decision" align="right">
            Each waiting order is bucketed by how long it has sat with the dispatch
            team since the last handoff. Under 24 hours is normal flow; anything
            past 7 days is a red flag worth asking about.
          </InfoTip>
        </div>
        <div className="grid grid-cols-4 gap-1 text-center">
          <AgeBucket label="<24h" count={sc.aging.fresh_lt_24h} tone="ok"
                     onClick={() => onDrill('kalpana_queue', { bucket: 'fresh_lt_24h' })} />
          <AgeBucket label="1-2d" count={sc.aging.aged_24_48h} tone="warn"
                     onClick={() => onDrill('kalpana_queue', { bucket: 'aged_24_48h' })} />
          <AgeBucket label="2-7d" count={sc.aging.aged_2_7d} tone="warn"
                     onClick={() => onDrill('kalpana_queue', { bucket: 'aged_2_7d' })} />
          <AgeBucket label=">7d" count={sc.aging.aged_gt_7d} tone="alarm"
                     onClick={() => onDrill('kalpana_queue', { bucket: 'aged_gt_7d' })} />
        </div>
        <button onClick={() => onDrill('kalpana_queue')}
                className="mt-1.5 text-xs text-blue-600 hover:underline">
          see all {fmtNum(sc.waitingTotal)} waiting →
        </button>
      </div>
      <Row label="MBO receipt → vendor dispatch (avg, last 7d)"
           value={sc.avgHoursMboToDispatch.last7d != null ? `${sc.avgHoursMboToDispatch.last7d}h` : '—'} />
      <HoursDelta last7d={sc.avgHoursMboToDispatch.last7d} prior7d={sc.avgHoursMboToDispatch.prior7d} />
      <div className="rounded-lg border border-gray-200 p-3">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1 flex items-center gap-1">
          <button onClick={() => onDrill('exceptions_7d')} className="hover:underline uppercase tracking-wide"
                  title="See every exception with order and who handled it">
            Real exceptions handled (last 7d) · <strong>{sc.exceptionsTotal}</strong>
          </button>
          <InfoTip title="Real exceptions handled">
            The judgement calls the dispatch team made this week — holds,
            cancellations, out-of-stock conversations, reships, substitutes.
            Routine progress (uploading, dispatching, delivering) is not
            counted, so this number is a fair measure of "problems handled",
            not busywork. Click the total or any reason for the exact orders;
            each reason has its own "i" explaining what it means.
          </InfoTip>
        </div>
        {sc.exceptionsThisWeek.length === 0 ? (
          <div className="text-xs text-gray-500">No exceptions handled in this window.</div>
        ) : (
          <ul className="space-y-1">
            {sc.exceptionsThisWeek.slice(0, 6).map((e) => (
              <li key={e.reason_code} className="flex items-center gap-1">
                <button onClick={() => onDrill('exceptions_7d', { reason: e.reason_code })}
                        title="See which orders these are"
                        className="flex-1 flex justify-between text-xs rounded px-1 -mx-1 hover:bg-blue-50 text-left min-w-0">
                  <span className="text-blue-600 hover:underline truncate">
                    {e.reason_code.replace(/_/g, ' ')}
                  </span>
                  <span className="font-medium tabular-nums ml-2">{e.count}</span>
                </button>
                <InfoTip title={e.reason_code.replace(/_/g, ' ')}>
                  {REASON_INFO[e.reason_code] || 'An operator decision recorded on the order — open the list to see the orders involved.'}
                </InfoTip>
              </li>
            ))}
          </ul>
        )}
      </div>
      {overAlertCount > 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          {sc.aging.aged_gt_7d > 0 && (
            <><strong>{sc.aging.aged_gt_7d}</strong> order(s) waiting > 7 days. </>
          )}
          {sc.aging.aged_24_48h > 0 && (
            <><strong>{sc.aging.aged_24_48h}</strong> aged 24-48h.</>
          )}
        </div>
      )}
    </ScorecardShell>
  );
}

function AgeBucket({ label, count, tone, onClick }) {
  const tones = {
    ok:    'bg-emerald-50 text-emerald-700 border-emerald-100',
    warn:  'bg-amber-50 text-amber-700 border-amber-100',
    alarm: 'bg-rose-50 text-rose-700 border-rose-100',
  };
  return (
    <button onClick={onClick} disabled={count === 0}
            title={count > 0 ? 'See which orders these are' : undefined}
            className={`rounded border px-2 py-1.5 ${count > 0 ? `${tones[tone]} hover:ring-2 hover:ring-blue-300 cursor-pointer` : 'bg-white text-gray-400 border-gray-200'}`}>
      <div className="text-[10px] uppercase tracking-wide">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{count}</div>
    </button>
  );
}

function VendorTrend({ mtd, prev }) {
  if (mtd == null || prev == null || prev === 0) return null;
  const diff = mtd - prev;
  if (diff === 0) return <span className="text-[11px] text-gray-500 inline-flex items-center gap-1"><Minus className="w-3 h-3" /> flat</span>;
  const up = diff > 0;
  const Icon = up ? ArrowUp : ArrowDown;
  const cls = up ? 'text-emerald-600' : 'text-rose-600';
  const pct = Math.abs(Math.round((diff / prev) * 100));
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${cls}`}>
      <Icon className="w-3 h-3" /> {pct}% vs last month
    </span>
  );
}

function VendorsScorecard({ vendors, onDrill }) {
  if (!vendors) return null;
  return (
    <ScorecardShell icon={Store} title="Vendors" accent="indigo"
                    info={<>
                      One card per vendor with orders this month.
                      <br />• <b>Fill rate</b> — shipments where the vendor shipped everything ÷ all their shipments. Red below 95%.
                      <br />• <b>OOS</b> — out-of-stock responses this month. Red above 10.
                      <br />• <b>Avg ship days</b> — dispatch email → tracking number received. Red above 3 days.
                      <br />• <b>INR payable</b> — quantity × the price effective on each dispatch date, plus shipping.
                      <br />• <b>Unpriced lines</b> — dispatched products with no price on file; any red here understates the payable.
                    </>}
                    subtitle={`${vendors.length} active this month`}>
      {vendors.length === 0 ? (
        <div className="text-xs text-gray-500 py-4 text-center">
          No vendor orders this month yet.
        </div>
      ) : (
        <div className="space-y-3">
          {vendors.map((v) => (
            <div key={v.vendorId} className="rounded-lg border border-gray-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-gray-900">{v.vendorName}</div>
                <VendorTrend mtd={v.ordersMtd} prev={v.prevMonthOrders} />
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                <Cell k="Orders MTD" v={fmtNum(v.ordersMtd)}
                      onClick={() => onDrill('vendor_orders_mtd', { vendorId: v.vendorId })} />
                <Cell k="Fill rate" v={v.fillRatePct != null ? `${v.fillRatePct}%` : '—'} alert={v.fillRatePct != null && v.fillRatePct < 95}
                      onClick={v.oosMtd > 0 ? () => onDrill('vendor_oos_mtd', { vendorId: v.vendorId }) : undefined} />
                <Cell k="OOS this month" v={fmtNum(v.oosMtd)} alert={v.oosMtd > 10}
                      onClick={v.oosMtd > 0 ? () => onDrill('vendor_oos_mtd', { vendorId: v.vendorId }) : undefined} />
                <Cell k="Avg ship days" v={v.avgShipDays != null ? `${v.avgShipDays}d` : '—'} alert={v.avgShipDays != null && v.avgShipDays > 3} />
                <Cell k="INR payable" v={fmtInr(v.inrTotalDue)} />
                <Cell k="Unpriced lines" v={fmtNum(v.unpricedLines)} alert={v.unpricedLines > 0} />
              </div>
            </div>
          ))}
        </div>
      )}
    </ScorecardShell>
  );
}

function Cell({ k, v, alert, onClick }) {
  const body = (
    <>
      <span className={onClick ? 'text-blue-600' : 'text-gray-500'}>{k}</span>
      <span className={`font-semibold tabular-nums ${alert ? 'text-rose-700' : 'text-gray-900'}`}>{v}</span>
    </>
  );
  if (onClick) {
    return (
      <button onClick={onClick} title="See which orders these are"
              className="flex items-baseline justify-between gap-2 w-full rounded px-1 -mx-1 hover:bg-blue-50">
        {body}
      </button>
    );
  }
  return <div className="flex items-baseline justify-between gap-2">{body}</div>;
}

function OpsScorecard({ ops, onDrill }) {
  if (!ops) return null;
  return (
    <ScorecardShell icon={Truck} title="Ops" accent="emerald"
                    infoAlign="left"
                    info="Shipment health, owned by the Ops team. 'Active TNs' are tracking numbers still moving with India Post. Days in transit count from when the tracking number was recorded. Parcels stuck over 14 days need chasing with India Post before the customer complains. 'Returned' means India Post sent the parcel back."
                    subtitle={`${fmtNum(ops.activeTns)} active TNs`}>
      <Row label="Active tracking numbers" value={fmtNum(ops.activeTns)}
           onClick={() => onDrill('active_tns')} />
      <Row label="Avg days in transit"
           value={ops.avgDaysInTransit != null ? `${ops.avgDaysInTransit}d` : '—'}
           hint={ops.maxDaysInTransit != null ? `max ${ops.maxDaysInTransit}d` : null}
           onClick={() => onDrill('active_tns')} />
      <Row label="Stuck in transit > 14 days"
           value={fmtNum(ops.stuckInTransitGt14d)}
           alert={ops.stuckInTransitGt14d > 0}
           onClick={() => onDrill('stuck_in_transit_gt_14d')} />
      <Row label="Orders currently owned by Ops" value={fmtNum(ops.ordersWithOps)}
           onClick={() => onDrill('orders_with_ops')} />
      <Row label="Delivered (last 7d)" value={fmtNum(ops.deliveredLast7d)}
           onClick={() => onDrill('delivered_last_7d')} />
      <Row label="Returned this month"
           value={fmtNum(ops.returnedMtd)}
           alert={ops.returnedMtd > 0}
           onClick={() => onDrill('returned_mtd')} />
      {ops.stuckInTransitGt14d > 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-auto">
          <strong>{fmtNum(ops.stuckInTransitGt14d)}</strong> shipment(s) sitting in transit over 14 days — customers will be asking.
        </div>
      )}
    </ScorecardShell>
  );
}
